import { useState } from "react";
import { useUserReviews, useDeleteReview, useUpdateReview } from "@/hooks/useReviewActions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Pencil, Trash2, Star, Calendar, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { Link } from "wouter";

export default function MyReviews() {
  const { data: reviews, isLoading } = useUserReviews();
  const deleteReview = useDeleteReview();
  const updateReview = useUpdateReview();
  
  const [editingReview, setEditingReview] = useState<any>(null);
  const [editText, setEditText] = useState("");
  const [editRating, setEditRating] = useState(5);

  const handleEdit = (review: any) => {
    setEditingReview(review);
    setEditText(review.text || "");
    setEditRating(review.rating);
  };

  const handleSaveEdit = async () => {
    if (!editingReview) return;
    
    await updateReview.mutateAsync({
      reviewId: editingReview.id,
      updates: { text: editText, rating: editRating }
    });
    setEditingReview(null);
  };

  const handleDelete = async (reviewId: string) => {
    await deleteReview.mutateAsync(reviewId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-full">
        <div className="text-muted-foreground">Loading your reviews...</div>
      </div>
    );
  }

  return (
    <div className="container max-w-2xl mx-auto p-4 pb-20">
      <div className="flex items-center gap-3 mb-6">
        <Link href="/profile">
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">My Reviews</h1>
      </div>

      {!reviews || reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">You haven't written any reviews yet.</p>
            <Link href="/feed">
              <Button className="mt-4" data-testid="button-start-reviewing">
                Start Reviewing
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reviews.map((review: any) => (
            <Card key={review.id} data-testid={`card-review-${review.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium">{review.rating}/5</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(review)}
                      data-testid={`button-edit-${review.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`button-delete-${review.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Review?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your review.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(review.id)}
                            data-testid={`button-confirm-delete-${review.id}`}
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm mb-3">{review.text || "No review text"}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  <span>{review.createdAt ? format(new Date(review.createdAt), 'MMM d, yyyy') : 'Unknown date'}</span>
                  <span className="mx-1">|</span>
                  <span>{review.likes || 0} likes</span>
                </div>
                {review.tags && review.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {review.tags.map((tag: string, idx: number) => (
                      <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!editingReview} onOpenChange={(open) => !open && setEditingReview(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Review</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setEditRating(star)}
                    data-testid={`button-rating-${star}`}
                  >
                    <Star
                      className={`w-6 h-6 cursor-pointer ${star <= editRating ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Review Text</label>
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                placeholder="Write your review..."
                rows={4}
                data-testid="input-review-text"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingReview(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateReview.isPending}
              data-testid="button-save-edit"
            >
              {updateReview.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
